import Completedtour from '@assets/images/onBoarding/Completedtour.png';
import { useProfile } from '@hooks/getQueries';
import { Box, Typography } from '@mui/material';

const TOTAL_STEPS = 13;

const StepContent = ({ step, totalSteps, description }) => (
  <Box>
    <Typography
      align="center"
      variant="body2"
      mb={1.5}
      sx={{ color: (theme) => theme.palette.grey[600] }}
    >
      Step {step} of {totalSteps}
    </Typography>
    <Typography variant="body2">{description}</Typography>
  </Box>
);

const stepsConfig = (userName) => [
  {
    target: 'body',
    content: (
      <Box textAlign="center" sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Welcome {userName} 🎉
        </Typography>
        <Typography variant="h6" gutterBottom>
          Let’s take a quick tour
        </Typography>
        <Typography variant="body2" mt={2}>
          Your qualitative research journey starts here. Learn how to create Surveys, AI Interviews,
          and Participants. Start the tour, skip it, or come back anytime.
        </Typography>
      </Box>
    ),
    placement: 'center',
    disableScrolling: true,
    disableBeacon: true,

    styles: { options: { width: 500 } },
  },
  {
    target: '[data-tour="dynamic-surveys"]',
    title: (
      <Typography variant="subtitle2" sx={{ ml: 2 }}>
        Create Surveys in Minutes
      </Typography>
    ),
    content: (
      <StepContent
        step={1}
        totalSteps={TOTAL_STEPS}
        description="Create AI-powered surveys, share via link or QR, capture voice responses, and get instant insights. You can also upload existing data for instant AI insights."
      />
    ),
    disableBeacon: true,
    spotlightPadding: 0,
    placement: 'right',
    showSkipButton: true,
    styles: { spotlight: { borderRadius: '4px' } },
  },
  {
    target: '[data-tour="interviews"]',
    title: (
      <Typography variant="subtitle2" sx={{ ml: 2 }}>
        Conduct Interviews with AI
      </Typography>
    ),
    content: (
      <StepContent
        step={2}
        totalSteps={TOTAL_STEPS}
        description="Conduct AI moderated interviews at scale across languages. Get real-time transcripts, themes, and insights. You can also upload existing files (text, audio & video) for instant analysis."
      />
    ),
    disableBeacon: true,
    spotlightPadding: 0,
    placement: 'right',
    showSkipButton: true,
    styles: { spotlight: { borderRadius: '4px' } },
  },
  {
    target: '[data-tour="ai-participants"]',
    title: (
      <Typography variant="subtitle2" sx={{ ml: 2 }}>
        Utilize AI Participants
      </Typography>
    ),
    content: (
      <StepContent
        step={3}
        totalSteps={TOTAL_STEPS}
        description="generate AI participants. Filter profiles, ask follow-ups, and get realistic responses fast. Send existing studies to AI Participants directly."
      />
    ),
    disableBeacon: true,
    spotlightPadding: 0,
    placement: 'right',
    showSkipButton: true,
    styles: { spotlight: { borderRadius: '4px' } },
  },
  {
    target: '[data-tour="quick-actions"]',
    title: (
      <Typography variant="subtitle2" sx={{ ml: 2 }}>
        Jump Right Into Action
      </Typography>
    ),
    content: (
      <StepContent
        step={4}
        totalSteps={TOTAL_STEPS}
        description="Use Quick Actions to instantly create a Survey, Interview, or generate AI Participants. Get started right away with no extra steps."
      />
    ),
    disableBeacon: true,
    spotlightPadding: 0,
    placement: 'top',
    showSkipButton: true,
    styles: { spotlight: { borderRadius: '4px' } },
  },
  {
    target: '[data-tour="recent-studies"]',
    title: (
      <Typography variant="subtitle2" sx={{ ml: 2 }}>
        View & Manage Recent Studies
      </Typography>
    ),
    content: (
      <StepContent
        step={5}
        totalSteps={TOTAL_STEPS}
        description="Your recent studies appear here. Edit, preview, share, collaborate, archive, or delete them with ease."
      />
    ),
    disableBeacon: true,
    spotlightPadding: 0,
    placement: 'top',
    showSkipButton: true,
    styles: { spotlight: { borderRadius: '4px' } },
  },
  {
    target: '[data-tour="workspace-switcher"]',
    title: (
      <Typography variant="subtitle2" sx={{ ml: 2 }}>
        Switch Between Workspaces
      </Typography>
    ),
    content: (
      <StepContent
        step={6}
        totalSteps={TOTAL_STEPS}
        description="Click here to switch between different workspaces or create new ones. Each workspace has its own studies, members, and billing."
      />
    ),
    disableBeacon: true,
    spotlightPadding: 3,
    placement: 'bottom',
    showSkipButton: true,
    styles: { spotlight: { borderRadius: '4px' } },
  },
  {
    target: '[data-tour="workspace-selector"]',
    title: (
      <Typography variant="subtitle2" sx={{ ml: 2 }}>
        Current Workspace Info
      </Typography>
    ),
    content: (
      <StepContent
        step={7}
        totalSteps={TOTAL_STEPS}
        description="View your current workspace details including subscription type and member count. This is different from the workspace switcher."
      />
    ),
    disableBeacon: true,
    spotlightPadding: 3,
    placement: 'bottom',
    showSkipButton: true,
    styles: { spotlight: { borderRadius: '4px' } },
  },
  {
    target: '[data-tour="credits"]',
    title: (
      <Typography variant="subtitle2" sx={{ ml: 2 }}>
        Keep Track of Your Credits
      </Typography>
    ),
    content: (
      <StepContent
        step={8}
        totalSteps={TOTAL_STEPS}
        description="Your credit balance appears here. Credits are used for AI- Moderated Interviews, AI analysis, AI responses."
      />
    ),
    disableBeacon: true,
    spotlightPadding: 3,
    placement: 'bottom',
    showSkipButton: true,
    styles: { spotlight: { borderRadius: '4px' } },
  },
  {
    target: '[data-tour="profile-settings"]',
    title: (
      <Typography variant="subtitle2" sx={{ ml: 2 }}>
        Manage your Account Settings
      </Typography>
    ),
    content: (
      <StepContent
        step={9}
        totalSteps={TOTAL_STEPS}
        description="You can update your personal info and account settings."
      />
    ),
    placement: 'left',
    showSkipButton: true,
    disableBeacon: true,
    spotlightPadding: 0,
    styles: { spotlight: { borderRadius: '4px' } },
  },
  {
    target: '[data-tour="explore-demos"]',
    title: (
      <Typography variant="subtitle2" sx={{ ml: 2 }}>
        Explore Tutorials & Demos
      </Typography>
    ),
    content: (
      <StepContent
        step={10}
        totalSteps={TOTAL_STEPS}
        description="Watch short tutorials to learn how to use different features."
      />
    ),
    placement: 'right',
    showSkipButton: true,
    disableBeacon: true,
    spotlightPadding: 0,
    styles: { spotlight: { borderRadius: '4px' } },
  },
  {
    target: '[data-tour="user-guide"]',
    title: (
      <Typography variant="subtitle2" sx={{ ml: 2 }}>
        Step-by-Step Guides
      </Typography>
    ),
    content: (
      <StepContent
        step={11}
        totalSteps={TOTAL_STEPS}
        description="Follow step-by-step guides with detailed walkthrough, screenshots and tips to help you navigate."
      />
    ),
    placement: 'right',
    showSkipButton: true,
    disableBeacon: true,
    spotlightPadding: 0,
    styles: { spotlight: { borderRadius: '4px' } },
  },
  {
    target: '[data-tour="help-support"]',
    title: (
      <Typography variant="subtitle2" sx={{ ml: 2 }}>
        Get Support
      </Typography>
    ),
    content: (
      <StepContent
        step={12}
        totalSteps={TOTAL_STEPS}
        description="Need assistance? We will be glad to help you."
      />
    ),
    placement: 'right',
    showSkipButton: true,
    disableBeacon: true,
    spotlightPadding: 0,
    styles: { spotlight: { borderRadius: '4px' } },
  },
  {
    target: '[data-tour="reset-tour"]',
    content: (
      <StepContent
        step={13}
        totalSteps={TOTAL_STEPS}
        description="To restart the guided tour, select your profile icon, and open Guided Tour."
      />
    ),
    placement: 'bottom',
    showSkipButton: true,
    disableBeacon: true,
    spotlightPadding: 3,
    styles: { spotlight: { borderRadius: '4px' } },
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    content: (
      <Box textAlign="center">
        <img
          src={Completedtour}
          alt="Completed Tour"
          style={{ width: '100px', marginBottom: '10px' }}
        />
        <Typography variant="h6">
          That’s it! You’re all set to explore the platform on your own.
        </Typography>
      </Box>
    ),
    hideBackButton: true,
    disableScrollParentFix: true,
  },
];

export const getDefaultSteps = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data: profileData } = useProfile();
  const userName = profileData?.first_name;

  return stepsConfig(userName);
};
